// File Name: pages/match-pages/ReferralPage.js
//
// /r/:code — the page a YNO referral link lands on.
//
// The app shares `https://nellab.org/r/AB12CD` into WhatsApp. What happens next
// depends on something this page cannot see, so it is built to be right either
// way:
//
//   * APP INSTALLED — Android never loads this page at all. The URL matches the
//     App Link intent filter in the app's AndroidManifest and opens YNO
//     directly, which is why the path is `/r/<code>` and not a query string.
//     (That hand-off is verified against /.well-known/assetlinks.json; if that
//     file is wrong the link silently falls through to this page instead, and
//     the page still works — it just costs the visitor a tap.)
//
//   * APP NOT INSTALLED — this page renders, and its job is to get the code to
//     the Play Store, where `referrer=` survives the install and the app reads
//     it back on first launch via the Install Referrer API. That is the ONLY
//     mechanism that carries a code through an install; Firebase Dynamic Links
//     used to do it and was shut down in August 2025.
//
// The code is also shown in full, large, with a copy button. Belt and braces:
// the referrer string does not survive every install route (sideloads, some
// OEM stores, a phone with no Play Store), and someone can always type six
// characters at sign-up.

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { normaliseCode } from '../../lib/ynoMatchDb';
import { ANDROID_PACKAGE, PLAY_STORE_URL } from './matchConfig';
import { useMatchLang } from './matchI18n';
import { MatchShell } from './MatchShell';

/**
 * The Play Store URL with the referral code attached.
 *
 * `referrer` is the parameter the Install Referrer API reads back, and the
 * Store passes it through verbatim. It is sent as a query string
 * (`referrer=utm_source%3Dyno_referral%26referrer%3DAB12CD`) rather than a bare
 * code because that is the shape the Play Console's own campaign tooling
 * produces, and `ReferralLinkService._codeFromReferrer` parses it as one —
 * a bare code still works, but this keeps the install attributable in the
 * console too.
 */
function storeUrlWithReferrer(code) {
  if (!PLAY_STORE_URL) return null;
  const referrer = `utm_source=yno_referral&utm_medium=share&referrer=${encodeURIComponent(
    code
  )}`;
  const sep = PLAY_STORE_URL.includes('?') ? '&' : '?';
  return `${PLAY_STORE_URL}${sep}referrer=${encodeURIComponent(referrer)}`;
}

export default function ReferralPage() {
  const { lang, t, toggle } = useMatchLang();
  const { code: codeParam } = useParams();
  const [params] = useSearchParams();

  // Tolerate every shape the link degrades into once it has been forwarded a
  // few times: /r/AB12CD, /r?code=AB12CD, /r?ref=AB12CD.
  const code = normaliseCode(
    codeParam || params.get('code') || params.get('ref') || ''
  );

  const [copied, setCopied] = useState(false);
  const storeUrl = storeUrlWithReferrer(code);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      // Clipboard is blocked outside a secure context and inside some in-app
      // browsers. The code is on screen in 38px type; nothing is lost.
    }
  }, [code]);

  // Reset the "copied" tick so the button doesn't stay confirmed forever.
  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  // A link with no usable code at all — forwarded as plain text and mangled,
  // most likely. Say so rather than showing an empty code box and a store
  // button that would attribute the install to nobody.
  if (!code) {
    return (
      <MatchShell t={t} lang={lang} toggle={toggle}>
        <div className="card center">
          <h1 className="h1">{t('refWelcome')}</h1>
          <p className="sub">{t('refNoCode')}</p>
          {PLAY_STORE_URL && (
            <a
              className="primary"
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('downloadYno')}
            </a>
          )}
        </div>
      </MatchShell>
    );
  }

  return (
    <MatchShell t={t} lang={lang} toggle={toggle}>
      <div className="card center">
        <p className="eyebrow">🎁</p>
        <h1 className="h1">{t('refWelcome')}</h1>
        <p className="sub">{t('refGetApp')}</p>

        <p className="eyebrow" style={{ marginTop: 22 }}>
          {t('refCodeLabel')}
        </p>
        <p
          style={{
            fontFamily: '"Barlow Condensed", sans-serif',
            fontSize: 44,
            fontWeight: 800,
            letterSpacing: 6,
            margin: '4px 0 14px',
          }}
        >
          {code}
        </p>

        <button type="button" className="secondary" onClick={copy}>
          {copied ? t('refCopied') : t('refCopy')}
        </button>

        {storeUrl ? (
          <>
            {/* The code rides along in `referrer=`, so a visitor who installs
                from here never has to type it — the app fills it in itself. */}
            <a
              className="primary"
              style={{ marginTop: 12 }}
              href={storeUrl}
              rel="noopener noreferrer"
            >
              {t('downloadYno')}
            </a>
            <p className="sub" style={{ marginTop: 12 }}>
              {t('refAutoApplied')}
            </p>
          </>
        ) : (
          // PLAY_STORE_URL is deliberately nullable while the listing does not
          // exist — see matchConfig. Better an honest sentence than a button
          // that 404s on Play.
          <p className="sub" style={{ marginTop: 16 }}>
            {t('notOnStores')}
          </p>
        )}

        <p className="sub" style={{ marginTop: 18, opacity: 0.7 }}>
          {t('refEnterOnSignup')}
        </p>
      </div>

      {/* Only useful to someone who has the app but landed here anyway —
          i.e. App Link verification did not take. One tap gets them across. */}
      <div className="card center">
        <p className="eyebrow">{t('openInApp')}</p>
        <a className="secondary" href={`intent://r/${code}#Intent;scheme=https;package=${ANDROID_PACKAGE};end`}>
          {t('refOpenApp')}
        </a>
      </div>
    </MatchShell>
  );
}
